import React, { useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { queryClient } from '@/App';
import { ActivityFormSchema } from '@/constants/formSchema/ActivityFormSchema';
import api from '@/services/apiSgft';
import { ActivityData, EmployeeTypeEnum } from '@/types/ActivityData';

import { Button } from '../atoms/Button';
import Modal from '../atoms/Modal';
import UncontrolledInputText from '../atoms/UncontrolledInputText';
import { ActivityPole } from './ActivityPole';
import ExpandableSubtitle from './ExpandableSubtitle';

interface ActivityModalProps {
	isOpen: boolean;
	onClose: () => void;
	type: 'create' | 'edit';
	activity?: ActivityData;
}
type ActivityForm = z.infer<typeof ActivityFormSchema>;

const ActivityModal: React.FC<ActivityModalProps> = ({
	isOpen,
	onClose,
	type,
	activity,
}) => {
	const [isSubmiting, setIsSubmiting] = useState(false);

	const transformActivityDataToFormSchema = (
		data: ActivityData,
	): z.infer<typeof ActivityFormSchema> => {
		const groupedData = data.activityPoles.reduce(
			(acc, poleWrapper) => {
				poleWrapper.activityPoleConditions.forEach((condition) => {
					const trainingType =
						condition.activityPoleConditionTrainings[0]?.training
							?.trainingType ?? '';
					const employeeTypeKey =
						condition.employeeType === EmployeeTypeEnum.INTERNAL
							? 'internalData'
							: 'thirdPartyData';

					if (!acc[employeeTypeKey].activityPoles[poleWrapper.id]) {
						acc[employeeTypeKey].activityPoles[poleWrapper.id] = {
							poleId: poleWrapper.pole.id,
							conditions: [],
						};
					}

					acc[employeeTypeKey].activityPoles[poleWrapper.id].conditions.push({
						id: condition.id,
						trainingType,

						trainings: condition.activityPoleConditionTrainings.map((apt) => ({
							trainingId: apt.training.id,
							name: apt.training.name,
						})),
					});
				});

				return acc;
			},
			{
				internalData: {
					activityPoles: {} as Record<
						number,
						z.infer<
							typeof ActivityFormSchema
						>['internalData']['activityPoles'][number]
					>,
				},
				thirdPartyData: {
					activityPoles: {} as Record<
						number,
						z.infer<
							typeof ActivityFormSchema
						>['thirdPartyData']['activityPoles'][number]
					>,
				},
			},
		);

		return {
			internalData: {
				activityPoles: Object.values(groupedData.internalData.activityPoles),
			},
			thirdPartyData: {
				activityPoles: Object.values(groupedData.thirdPartyData.activityPoles),
			},
			description: data.description,
			id: data.id,
			name: data.name,
		};
	};
	const getDefaultSchema = (): ActivityForm => ({
		description: '',
		id: null,
		name: '',
		internalData: {
			activityPoles: [
				{
					poleId: null,
					conditions: [
						{
							trainingType: null,
							trainings: [{ trainingId: null }],
							id: null,
						},
					],
				},
			],
		},
		thirdPartyData: {
			activityPoles: [
				{
					poleId: null,
					conditions: [
						{
							trainingType: null,
							trainings: [{ trainingId: null }],
							id: null,
						},
					],
				},
			],
		},
	});
	const form = useForm<ActivityForm>({
		defaultValues:
			type === 'edit' && activity
				? transformActivityDataToFormSchema(activity)
				: getDefaultSchema(),
	});

	const control = form.control;

	const closeAndClear = () => {
		form.reset();
		onClose();
	};
	function parseToSubmitData(form: ActivityForm) {
		const parsePoles = (
			poles: ActivityForm['internalData']['activityPoles'],
			employeeType: EmployeeTypeEnum,
		) => {
			return poles
				.filter((pole) => pole.poleId !== null) // opcional: ignora poles sem ID
				.map((pole) => ({
					poleId: pole.poleId,
					activityPoleConditions: pole.conditions.map((condition) => ({
						id: condition.id,
						employeeType,
						activityPoleConditionTrainings: condition.trainings
							.filter((training) => training.trainingId !== null)
							.map((training) => training.trainingId),
					})),
				}));
		};

		const internalPoles = parsePoles(
			form.internalData.activityPoles,
			EmployeeTypeEnum.INTERNAL,
		);
		const thirdPartyPoles = parsePoles(
			form.thirdPartyData.activityPoles,
			EmployeeTypeEnum.THIRD_PARTY,
		);
		const poleMap = new Map<
			number,
			{
				poleId: number;
				activityPoleConditions: {
					employeeType: EmployeeTypeEnum;
					activityPoleConditionTrainings: number[];
				}[];
			}
		>();

		[...internalPoles, ...thirdPartyPoles].forEach((pole) => {
			const poleId = pole.poleId as number;
			if (poleMap.has(poleId)) {
				const existing = poleMap.get(poleId)!;
				existing.activityPoleConditions.push(
					...(pole.activityPoleConditions as any),
				);
			} else {
				poleMap.set(poleId, {
					poleId,
					activityPoleConditions: [...pole.activityPoleConditions] as any,
				});
			}
		});
		return {
			name: form.name,
			description: form.description,
			activityPoles: Array.from(poleMap.values()),
		};
	}
	const onSubmit = async (data: ActivityForm) => {
		try {
			setIsSubmiting(true);
			const submitData = parseToSubmitData(data);
			if (type === 'create') {
				await api.post('/activity/', submitData);
			} else {
				await api.put(`/activity/${activity?.id}`, submitData);
			}
			toast.success(
				`Atividade! ${type === 'create' ? 'criada' : 'salva'} com sucesso!`,
				{
					theme: 'colored',
					toastId: 'success',
				},
			);
			queryClient.invalidateQueries(['get-all-activities-data']);
		} catch (error: any) {
			const errorMessage =
				error?.response?.status < 500
					? error?.response?.data?.message
					: `Erro ao ${type === 'create' ? 'criar' : 'salvar'} atividade!`;
			toast.error(errorMessage, {
				theme: 'colored',
				toastId: 'error',
			});
		}
		setIsSubmiting(false);
		closeAndClear();
	};

	const {
		fields: internalFields,
		append: appendInternal,
		remove: removeInternal,
	} = useFieldArray<z.infer<typeof ActivityFormSchema>>({
		control,
		name: 'internalData.activityPoles',
	});
	const {
		fields: thirdPartyFields,
		append: appendThirdParty,
		remove: removeThirdParty,
	} = useFieldArray<z.infer<typeof ActivityFormSchema>>({
		control,
		name: 'thirdPartyData.activityPoles',
	});

	return (
		<Modal
			isOpen={isOpen}
			onClose={closeAndClear}
			title={`${type === 'create' ? 'Cadastro de Atividade' : 'Edição de Atividade'}`}
			className="z-[100] w-[60rem] max-w-[85vw]"
		>
			<FormProvider {...form}>
				<form
					className="space-between flex h-[90%] flex-col px-5 py-2"
					onSubmit={form.handleSubmit(onSubmit)}
				>
					<div className="flex w-full flex-col items-center">
						<div className="flex w-full justify-between">
							<UncontrolledInputText
								title="Nome"
								required
								width="57rem"
								{...form.register('name')}
								upperCase
							/>
						</div>
						<div className="w-full">
							<ExpandableSubtitle
								subtitle="Treinamentos Obrigatórios para Primários"
								containerClassName="w-full"
							>
								<div className="flex w-full flex-col gap-2">
									{internalFields.map((pole, poleIndex) => {
										return (
											<ActivityPole
												employeeType={EmployeeTypeEnum.INTERNAL}
												key={`activity-pole-internal-${pole.id}`}
												poleId={pole.id}
												poleIndex={poleIndex}
												form={form}
												removePole={removeInternal}
											/>
										);
									})}
									<div className="my-3 flex">
										<button
											className="text-xs font-medium text-primary hover:opacity-80"
											onClick={() =>
												appendInternal({
													poleId: null,
													conditions: [
														{
															id: null,
															trainingType: null,
															trainings: [{ trainingId: null }],
														},
													],
												})
											}
											type="button"
										>
											+ Adicionar Polo
										</button>
									</div>
								</div>
							</ExpandableSubtitle>

							<ExpandableSubtitle
								subtitle="Treinamentos Obrigatórios para Terceiros"
								containerClassName="w-full"
							>
								<div className="flex w-full flex-col gap-2">
									{thirdPartyFields.map((pole, poleIndex) => {
										return (
											<ActivityPole
												employeeType={EmployeeTypeEnum.THIRD_PARTY}
												key={`activity-pole-third-party-${pole.id}`}
												poleId={pole.id}
												poleIndex={poleIndex}
												form={form}
												removePole={removeThirdParty}
											/>
										);
									})}
									<div className="my-3 flex">
										<button
											className="text-xs font-medium text-primary hover:opacity-80"
											onClick={() =>
												appendThirdParty({
													poleId: null,
													conditions: [],
												})
											}
											type="button"
										>
											+ Adicionar Polo
										</button>
									</div>
								</div>
							</ExpandableSubtitle>
						</div>

						<div className="my-2 mt-[1.5rem] flex w-full justify-between">
							<Button
								type="button"
								className="h-8 min-w-36 border-2 border-[#193CB9] bg-white text-[#193CB9] hover:bg-[#e9eaf1]"
								onClick={closeAndClear}
							>
								Cancelar
							</Button>
							<Button
								type="submit"
								className="h-8 min-w-36"
								isLoading={isSubmiting}
							>
								Salvar
							</Button>
						</div>
					</div>
				</form>
			</FormProvider>
		</Modal>
	);
};

export default ActivityModal;
